import { notify, useModal } from '@billy/design-system'
import { trackError } from '@billy/track-error'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'react-query'

import type { UiSourceValue } from '@features/reconciliation/enums'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { ModalId } from '../../../enums/modalId'
import { NotificationKeys } from '../../../enums/notificationKeys'
import { useOrganizationViewUrl } from '../../../hooks/routing/useOrganizationViewUrl'
import { APIError, getCurrentLocale } from '../../../utils'
import { localeToLanguage } from '../../../utils/localeToLanguage'
import { reactRoute } from '../../../utils/routing/reactRoute'
import { OpenBankingConnectionError } from '../enums/openBankingConnectionError'
import { initiateOpenBankingConnection } from '../query-api'

export const useInitiateOpenBankingConnection = (uiSource?: UiSourceValue) => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const { open: openErrorModal } = useModal(ModalId.BankConnectionErrorModal)
  const locale = getCurrentLocale()
  const { url: dashboardUrl } = useOrganizationViewUrl(reactRoute.organization.dashboard.getMainRoute())

  const redirectUrl = `${window.location.origin}${dashboardUrl}`

  const {
    mutate,
    isLoading: isProcessing,
    ...rest
  } = useMutation(
    (bankId?: string | undefined) =>
      initiateOpenBankingConnection({
        bankId,
        organizationId: organization?.id as string,
        redirectUrl,
        locale: localeToLanguage[locale],
        countryCode: organization?.countryId as string,
        uiSource,
      }),
    {
      onSuccess: (response) => {
        if (response?.authUrl) {
          window.location.replace(response?.authUrl)
          return
        }
        trackError('Bank connection: Missing authUrl in response')
        openErrorModal()
      },
      onError: (error: APIError) => {
        if (error.statusCode === 400 && error.body?.type === OpenBankingConnectionError.EmailBounced) {
          notify(
            {
              id: NotificationKeys.BankReconciliationConnectionEmailBounced,
              title: t('bank_integration.connection_bounced_email.notification.title'),
              message: t('bank_integration.connection_bounced_email.notification.message'),
              variant: 'error',
            },
            { autoClose: false },
          )
        } else {
          openErrorModal()
        }
      },
    },
  )

  const connectToOpenBanking = (bankId?: string) => mutate(bankId)

  return {
    isProcessing,
    connectToOpenBanking,
    ...rest,
  }
}
